import { Button, Grid, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FormikHelpers, FormikProps } from 'formik';
import { getIn } from 'formik';
import { useFormik } from 'formik';
import { FTextInput } from '@pflegenavi/web-components';

import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useConnectServiceProviderAccountValidationSchema } from '@pflegenavi/shared/frontend';
import { CountryField } from '../profile/CountryField';

export interface IConnectAccountFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  address: string;
  zipCode: string;
  city: string;
  phone: string;
  countryId: string;
}

interface ConnectFormProps {
  companyDetails: {
    name: string;
    address: string;
    city: string;
    zipCode: string;
    phone: string;
    countryId: string;
  };
  userDetails: {
    firstName: string;
    lastName: string;
  };
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  loading: boolean;
  accountAffiliationName?: string;
  handleConnectAccount: (
    values: IConnectAccountFormValues,
    responseToInvitation?: boolean
  ) => Promise<void>;
}

// eslint-disable-next-line complexity
export const ConnectForm: FC<ConnectFormProps> = ({
  companyDetails,
  userDetails,
  countryOptions,
  loading,
  accountAffiliationName,
  handleConnectAccount,
}) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object(
    useConnectServiceProviderAccountValidationSchema()
  ).required();

  const [acceptInvitation, setAcceptInvitation] = useState<boolean | undefined>(
    undefined
  );

  const handleSubmit = useCallback(
    async (
      values: IConnectAccountFormValues,
      helpers: FormikHelpers<IConnectAccountFormValues>
    ) => {
      try {
        await handleConnectAccount(values, acceptInvitation);
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [handleConnectAccount, acceptInvitation]
  );

  const initialValues: IConnectAccountFormValues = useMemo(() => {
    return {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      companyName: companyDetails.name,
      address: companyDetails.address,
      zipCode: companyDetails.zipCode,
      city: companyDetails.city,
      phone: companyDetails.phone,
      countryId: companyDetails.countryId,
    };
  }, [userDetails, companyDetails]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: false, // To avoid the form "blinking" when the initialValues change e.g. during submit
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false, // To avoid the form being validate again during submission when the first request is finished
  });

  const handleSaveButtonClick = useCallback(
    async (acceptInvite?: boolean) => {
      setAcceptInvitation(acceptInvite); // No need to reset if form validation fails since it is newly set before submitting anyways
      await formik.submitForm();
    },
    [formik]
  );

  if (!accountAffiliationName) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{
          width: '100%',
          height: '100vh',
        }}
      >
        <Stack sx={{ maxWidth: 500 }} gap={3}>
          <Stack gap={0.5}>
            <Typography variant="h5" color="primary.main">
              {t('service-provider-app.connect-account.invitation-invalid')}
            </Typography>
            <Typography variant="subtitle2" color="grey.500">
              {t(
                'service-provider-app.connect-account.invitation-invalid-description'
              )}
            </Typography>
          </Stack>
          <ConnectFormInputs
            formik={formik}
            disabled={formik.isSubmitting || loading}
            countryOptions={countryOptions}
          />
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSaveButtonClick()}
            disabled={!formik.isValid || formik.isSubmitting || loading}
            fullWidth
          >
            {t('service-provider-app.connect-account.complete-account')}
          </Button>
          <Stack direction="row" justifyContent="center" gap={0.5}>
            <Typography variant="subtitle2" color="grey.500">
              {t('service-provider-app.connect-account.account-affiliation')}:
            </Typography>
            <Typography
              variant="subtitle2"
              color="primary.main"
              fontWeight={700}
            >
              {t('service-provider-app.connect-account.no-affiliation')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        width: '100%',
        height: '100vh',
      }}
    >
      <Stack sx={{ maxWidth: 500 }} gap={3}>
        <Stack gap={0.5}>
          <Typography variant="h5" color="primary.main">
            {t('service-provider-app.connect-account.you-are-invited')}
          </Typography>
          <Typography variant="subtitle2" color="grey.500">
            {t(
              'service-provider-app.connect-account.you-are-invited-description'
            )}
          </Typography>
        </Stack>
        <ConnectFormInputs
          formik={formik}
          countryOptions={countryOptions}
          disabled={formik.isSubmitting || loading}
        />
        <Stack direction="row" justifyContent="center" gap={1}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => handleSaveButtonClick(false)}
            disabled={!formik.isValid || formik.isSubmitting || loading}
            fullWidth
          >
            {t('service-provider-app.connect-account.save-without-connecting')}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSaveButtonClick(true)}
            disabled={!formik.isValid || formik.isSubmitting || loading}
            fullWidth
          >
            {t('service-provider-app.connect-account.connect-account')}
          </Button>
        </Stack>
        <Stack direction="row" justifyContent="center" gap={0.5}>
          <Typography variant="subtitle2" color="grey.500">
            {t('service-provider-app.connect-account.account-affiliation')}:
          </Typography>
          <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
            {accountAffiliationName}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

interface ConnectFormInputsProps {
  formik: FormikProps<IConnectAccountFormValues>;
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  disabled: boolean;
}

export const ConnectFormInputs: FC<ConnectFormInputsProps> = ({
  formik,
  countryOptions,
  disabled,
}) => {
  const { t } = useTranslation();

  const onBlur = useCallback(() => {
    formik.setFieldTouched('countryId', true);
  }, [formik]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.countryId === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        countryId: newCountryId,
      }));
      formik.setFieldError('countryId', undefined);
    },
    [formik]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="firstName"
          name="firstName"
          label={t('service-provider-app.connect-account.form.first-name')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="lastName"
          name="lastName"
          label={t('service-provider-app.connect-account.form.last-name')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FTextInput
          id="companyName"
          name="companyName"
          label={t('service-provider-app.connect-account.form.company-name')}
          formik={formik}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FTextInput
          id="address"
          name="address"
          label={t('service-provider-app.connect-account.form.address')}
          formik={formik}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="zipCode"
          name="zipCode"
          label={t('service-provider-app.connect-account.form.zip-code')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="city"
          name="city"
          label={t('service-provider-app.connect-account.form.city')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <CountryField
          onChangeCountry={onChangeCountry}
          onBlur={onBlur}
          countries={countryOptions}
          countryId={formik.values.countryId}
          disabled={disabled}
          name={'countryId'}
          errorMessage={
            getIn(formik.touched, 'countryId') &&
            getIn(formik.errors, 'countryId')
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FTextInput
          id="phone"
          type="tel"
          name="phone"
          label={t('service-provider-app.connect-account.form.phone')}
          formik={formik}
          fullWidth
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};
